/* eslint-disable react/prop-types */
import React, { useMemo } from 'react'
import { SectionList } from 'react-native'

import EmptyListPlaceholder from './components/EmptyListPlaceholder'
import SelectContainer from './components/SelectContainer'
import TokenNameFilterChips from './components/TokenNameFilterChips'
import { SectionedSelectProps } from './types'
import useSelectInternal from './useSelectInternal'

const SectionedSelect = ({
  setValue,
  value,
  sections,
  menuOptionHeight,
  headerHeight,
  renderSectionHeader,
  SectionSeparatorComponent,
  stickySectionHeadersEnabled,
  emptyListPlaceholderText,
  attemptToFetchMoreOptions,
  onSearch,
  testID,
  menuPosition,
  listHeaderComponent,
  ...props
}: SectionedSelectProps) => {
  const selectData = useSelectInternal({
    menuOptionHeight,
    setValue,
    value,
    headerHeight,
    stickySectionHeadersEnabled,
    data: sections,
    attemptToFetchMoreOptions,
    onSearch,
    menuPosition,
    // Without this the rows render in 'select' mode and miss the bottomSheet row
    // styling (rounded hover/selected highlight + spacing).
    mode: (props as any).mode,
    withNameFilterChips: (props as any).withNameFilterChips
  })
  const {
    listRef,
    filteredData,
    renderItem,
    keyExtractor,
    getItemLayout,
    handleScroll,
    handleLayout,
    setIsMenuOpen,
    nameFilterChips,
    control,
    setSearch
  } = selectData

  // Stable ListHeaderComponent reference — an inline arrow would change identity
  // on every parent re-render (e.g. row hover) and make SectionList remount the
  // header subtree, which re-mounts the pill TokenIcons and flashes their images.
  // `nameFilterChips` watches the search field itself (via `control`), so its
  // identity is stable across keystrokes and the header never remounts — that's
  // what keeps the listHeaderComponent's TokenIcons from flashing on each keypress.
  const ListHeaderComponent = useMemo(() => {
    const hasChips = nameFilterChips.length > 0
    if (!hasChips && !listHeaderComponent) return undefined
    return () =>
      (
        <>
          {hasChips && (
            <TokenNameFilterChips
              chips={nameFilterChips}
              control={control}
              setSearch={setSearch}
            />
          )}
          {typeof listHeaderComponent === 'function'
            ? listHeaderComponent({ closeMenu: () => setIsMenuOpen(false) })
            : listHeaderComponent}
        </>
      ) as any
  }, [nameFilterChips, control, setSearch, listHeaderComponent, setIsMenuOpen])

  return (
    <SelectContainer
      value={value}
      setValue={setValue}
      {...selectData}
      {...props}
      menuProps={{ ...selectData.menuProps, ...(props.menuProps || {}) }}
      id={testID}
      testID={testID}
    >
      <SectionList
        ref={listRef}
        sections={filteredData as SectionedSelectProps['sections']}
        renderItem={renderItem as any}
        onLayout={handleLayout}
        ListHeaderComponent={ListHeaderComponent}
        renderSectionHeader={renderSectionHeader}
        keyExtractor={keyExtractor}
        initialNumToRender={15}
        windowSize={10}
        maxToRenderPerBatch={20}
        SectionSeparatorComponent={SectionSeparatorComponent}
        removeClippedSubviews
        getItemLayout={getItemLayout as any}
        ListEmptyComponent={<EmptyListPlaceholder placeholderText={emptyListPlaceholderText} />}
        stickySectionHeadersEnabled={stickySectionHeadersEnabled}
        onScroll={handleScroll}
        scrollEventThrottle={16}
      />
    </SelectContainer>
  )
}

export default React.memo(SectionedSelect)
